import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { getFileThumbnail } from "~/services/common/fileThumbnail";

interface ThumbnailMetadata {
  thumbnailStatus?: string | null;
  thumbnailUrl?: string | null;
}

const readyUrl = (data?: ThumbnailMetadata) =>
  data?.thumbnailStatus === "COMPLETE" && data.thumbnailUrl
    ? data.thumbnailUrl
    : undefined;

const isGenerating = (data?: ThumbnailMetadata) =>
  data?.thumbnailStatus === "PENDING" ||
  (data?.thumbnailStatus === "COMPLETE" && !data.thumbnailUrl);

const MAX_POLL_TIME = 5 * 60_000;

export const useAttachmentThumbnail = (
  fileSeq: number,
  metadata: ThumbnailMetadata,
  enabled: boolean,
) => {
  const pollingStartedAt = useRef(Date.now());
  const canFetch = enabled && Number.isSafeInteger(fileSeq) && fileSeq > 0;
  const query = useQuery({
    queryKey: ["attachment-thumbnail", fileSeq],
    queryFn: ({ signal }) => getFileThumbnail(fileSeq, signal),
    enabled: canFetch && !readyUrl(metadata),
    staleTime: (current) => (readyUrl(current.state.data) ? Infinity : 0),
    gcTime: 10 * 60_000,
    retry: (count, error) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      return count < 1 && (!status || status === 429 || status >= 500);
    },
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchInterval: (current) => {
      if (
        current.state.status === "error" ||
        !isGenerating(current.state.data) ||
        Date.now() - pollingStartedAt.current >= MAX_POLL_TIME
      ) {
        return false;
      }

      return Math.min(
        15_000,
        3_000 * 2 ** Math.floor(current.state.dataUpdateCount / 3),
      );
    },
  });

  // 네이티브는 fileSeq/fileUrl만 전달하므로 웹뷰에서 조회한 메타데이터로 표시한다.
  const thumbnailUrl = readyUrl(query.data) ?? readyUrl(metadata);
  const data = query.data ?? metadata;
  const isDelayed = Date.now() - pollingStartedAt.current >= MAX_POLL_TIME;
  const state = thumbnailUrl
    ? "ready"
    : !canFetch
      ? "unavailable"
      : query.isError
        ? "error"
        : query.isPending
          ? "loading"
          : isGenerating(data)
            ? isDelayed ? "delayed" : "pending"
            : data.thumbnailStatus === "FAILED"
              ? "failed"
              : "unavailable";

  const retry = () => {
    if (!canFetch) return;
    pollingStartedAt.current = Date.now();
    void query.refetch();
  };

  return { thumbnailUrl, state, retry, isFetching: query.isFetching, canFetch } as const;
};
